import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Revalidator, RevalidatorOptions, SWRConfiguration } from 'swr';

import useProductStaticFormatted from './useProductStaticFormatted';
import useLargeSizeMode from './useLargeSizeMode';
import { useCustomSWR } from 'hooks/useCustomSWR';
import useEnv from 'hooks/useEnv';
import useURLQuery from 'hooks/useURLQuery';
import { FetchError } from 'exceptions/FetchError';
import { apiUrls } from 'services/api-urls';
import { fetcher } from 'services/fetcher';
import { getIdCustColor } from 'services/product';
import { hasOwnPropertyInsensitive } from 'services/objects';
import {
  FormattedProductDataType,
  FormattedProductDynamicDataType,
  ProductDynamicDataType,
  ProductKind,
  ProductStaticDataType,
  ProductVariantsColorsType,
} from 'types/ProductPageV2/Products';

type UseProductDataResult = {
  formattedProductData: Partial<FormattedProductDataType>;
  formattedProductDynamicData: FormattedProductDynamicDataType;
  productType: ProductKind;
  isMCProduct: boolean | undefined;
  isLoaded: boolean;
};

export default function useProductData(
  data: ProductStaticDataType,
  color: string | undefined,
  setColor: (color: string | undefined) => void
): UseProductDataResult {
  const { pathname, state } = useLocation();
  const { selected } = useURLQuery();
  const { country } = useEnv();
  const { LSModeEnabled: isLargeMode } = useLargeSizeMode();
  const removeCountry = (url: string) => url.replace(`${country}/`, '');
  const { r: productSku, rc: productRc } = getUrlProductSku();
  const forceSkuUrlAsVariant = !!selected;
  const staticData = useProductStaticFormatted(
    data,
    color,
    forceSkuUrlAsVariant ? productSku : undefined
  );

  const {
    data: dynamicData,
    isLoaded: isDynamicLoaded,
  } = useCustomSWR<ProductDynamicDataType>(
    apiUrls.getProductDynamic(removeCountry(pathname), isLargeMode),
    fetcher,
    {
      initialData: state?.searchApiData?.merchandising,
      onErrorRetry: onErrorRetryHandler,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (!staticData) return;

    const productColor = getColorBySku(productSku, staticData.variants);

    // Handle product change
    if (!productColor) setColor(undefined);

    // Handle history back state
    if (productColor && productColor !== color) setColor(productColor);
  }, [pathname]);

  function getUrlProductSku() {
    const startIndex = pathname.indexOf('P-') + 2;
    const endIndex = pathname.indexOf('-', startIndex);
    const productId = pathname.substring(startIndex, endIndex);

    return { r: productId.substring(0, 7), rc: productId.substring(0, 8) };
  }

  function getColorBySku(
    productSku: string,
    variants: ProductVariantsColorsType
  ): string | undefined {
    let color: string | undefined = undefined;

    for (const [key, colorVariants] of Object.entries(variants)) {
      if (hasOwnPropertyInsensitive(colorVariants, productSku)) {
        color = key;

        break;
      }
    }

    return color;
  }

  return {
    formattedProductData: {
      breadcrumb: staticData?.breadcrumb,
      product: staticData?.product,
      productAttributes: staticData?.productAttributes,
      variants: staticData?.variants,
      variantsAttributes: staticData?.variantsAttributes,
      colors: staticData?.colors,
      sizes: staticData?.sizes,
      cups: staticData?.cups,
    },
    formattedProductDynamicData: {
      seo: dynamicData?.seo,
      reassurance: dynamicData?.reassurance?.htmlContent,
      offer: dynamicData?.offer?.htmlContent,
      relatedProducts: dynamicData?.relatedProducts?.products || [],
      similarProducts: dynamicData?.similarProducts?.products || [],
      linkedProducts: dynamicData?.linkedProducts?.products || [],
      lastVisitedProducts: dynamicData?.lastVisitedProducts?.products || [],
    },
    productType: staticData?.productType ?? 0,
    isMCProduct:
      staticData && productRc === getIdCustColor(staticData.product.Attributes),
    isLoaded: isDynamicLoaded,
  };
}

const onErrorRetryHandler = (
  error: FetchError,
  _key: string,
  config: SWRConfiguration,
  revalidate: Revalidator,
  { retryCount }: RevalidatorOptions
): void => {
  if (error.status === 404) {
    return;
  }

  setTimeout(() => revalidate({ retryCount }), config.errorRetryInterval);
};
